import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import Box, { Props as BoxProps } from 'common/components/box/Box';
import MonthlyRouteChart from './MonthlyRouteChart';
import { ReactComponent as ArrowDown } from 'assets/svg/chevron_down.svg';
import Button from 'common/components/button/Button';
import Colors from 'styles/export/colors.module.scss'
import styles from './MonthlyRouteChartBox.module.scss';
import { MonthlyRouteDashboardDto, MonthlyRoutesDashboardDto } from 'api/dashboard/dtos/MonthlyRouteDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';

type Props = {
	// Legacy props (direct data passing)
	data?: MonthlyRoutesDashboardDto | null;
	isLoading?: boolean;
	// New props (self-fetch mode)
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
} & Partial<BoxProps>;

const MonthlyRouteChartBox = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [fetchedData, setFetchedData] = useState<MonthlyRoutesDashboardDto | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);
	const [selectedYearIndex, setSelectedYearIndex] = useState<number | null>(null);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('monthlyRouteChart');
	const scopeVehicleId = useDashboardVehicleScope();
	const vehicleScope = vehicleScopeCriteria(scopeVehicleId);
	// No detalhe do ativo (scoped) o título é "do ativo" em vez de "da frota".
	const routesTitle = (scopeVehicleId ? t('dashboard.monthly_routes_vehicle' as any) : t('dashboard.monthly_routes')) as string;

	const isLegacyMode = props.data !== undefined;
	const isControlled = props.filters != null;

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			setFetchLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getMonthlyRoutes(criteria);
				if (!cancelled) {
					setFetchedData(result);
				}
			} catch {
				if (!cancelled) {
					addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				}
			} finally {
				if (!cancelled) {
					setFetchLoading(false);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode, dateFrom, dateTo]);

	const data = isLegacyMode ? props.data! : fetchedData;
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	const allYears = (data?.items || []).map((x: MonthlyRouteDashboardDto) => x.date.getFullYear());

	const yearsOptions = allYears
		.filter((year, i, self) => self.findIndex(d => d === year) === i)
		.sort((a, b) => a - b);

	const existsMoreThanOneYear = allYears.every((year, _, arr) => year === arr[0]) === false;

	const selectedYear = selectedYearIndex !== null ? yearsOptions[selectedYearIndex] : null;

	useEffect(() => {
		// In widget mode the WidgetMonthPicker controls the date range directly,
		// so we never auto-pick a single year — show all months that come back from the API.
		// The legacy year navigator (used in embedded mode) still works via the
		// onPreviousYear/onNextYear handlers below.
		if (isLegacyMode && existsMoreThanOneYear) {
			const currYear = dayjs().year();
			const currYearExistIndex = yearsOptions.findIndex(year => year === currYear);
			setSelectedYearIndex(currYearExistIndex !== -1 ? currYearExistIndex : 0);
		} else {
			setSelectedYearIndex(null);
		}
	}, [data, isLegacyMode]);

	const hasItems = !!data?.items && data.items.length > 0;

	if (!isLegacyMode && !isLoading && !hasItems) {
		return (
			<Box
				className={tableStyles.container}
				title={routesTitle}
				subTitleTooltip={t('dashboard.monthly_routes_tooltip' as any) as string}
				renderOptions={isControlled ? undefined : <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
			>
				<WidgetNoData />
			</Box>
		);
	}

	if (isLegacyMode && !data && !isLoading) {
		return null;
	}

	const onPreviousYear = () => {
		if (selectedYearIndex !== null && selectedYearIndex > 0) {
			setSelectedYearIndex(selectedYearIndex - 1);
		}
	};

	const onNextYear = () => {
		if (selectedYearIndex !== null && selectedYearIndex < yearsOptions.length - 1) {
			setSelectedYearIndex(selectedYearIndex + 1);
		}
	};

	const yearOptions = existsMoreThanOneYear && selectedYear
		? (
			<div className={styles.optionsContainer}>
				<Button preset="primary" size="extraSmall" bw={false} onClick={onPreviousYear} disabled={selectedYear === yearsOptions[0]}>
					<ArrowDown width={12} height={12} fill={Colors.white} style={{ transform: 'rotate(90deg)' }} />
				</Button>
				<div className={styles.year}>
					{selectedYear}
				</div>
				<Button preset="primary" size="extraSmall" bw={false} onClick={onNextYear} disabled={selectedYear === yearsOptions[yearsOptions.length - 1]}>
					<ArrowDown width={12} height={12} fill={Colors.white} style={{ transform: 'rotate(-90deg)' }} />
				</Button>
			</div>
		)
		: undefined;

	const effectiveDateFrom = props.filters?.dateFrom ?? dateFrom;
	const effectiveDateTo = props.filters?.dateTo ?? dateTo;

	const content = isLoading
		? <div className={styles.centerAlign}> <LoadingSpinner /></div>
		: <MonthlyRouteChart data={data} selectedYear={selectedYear} dateFrom={effectiveDateFrom} dateTo={effectiveDateTo} />;

	if (isLegacyMode) {
		return (
			<Box
				className={props.className}
				bodyClassName={props.bodyClassName}
				title={props.title}
				subTitle={props.subTitle}
				renderOptions={yearOptions}
			>
				{content}
			</Box>
		);
	}

	return (
		<Box
			className={tableStyles.container}
			title={routesTitle}
			subTitleTooltip={t('dashboard.monthly_routes_tooltip' as any) as string}
			renderOptions={isControlled ? undefined : <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			{content}
		</Box>
	);
};

export default MonthlyRouteChartBox;
